/**
 * The ways of starting robot control that staff have switched on (ops sets them; ui-options/BUILD-SPEC.md). In both
 * the Robotensor app on a phone drives the robot (AR, or touch control on phones without AR); the computer shows it.
 *   phone_scan     the task opens here, the phone's camera reads the code and the app opens already paired
 *   phone_handoff  "Continue on computer": the phone sends a task to a signed-in computer, or a code at /go
 * The server lists only the enabled ones, so nothing here may name, hint at or link to a mode outside the set.
 * The app config's `features.control_modes` decides when the server sent it (resilience.md §4); otherwise
 * `GET /v1/control-modes` does. Any failure (signed out, older server) reads as "nothing extra is on": the safe default.
 */
import type { ControlMode } from '@robotensor/api'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api.ts'
import { useAppConfig } from '../lib/appConfig.ts'

export type { ControlMode }

/** GET /api/platform/v1/control-modes: public, so /go and /p/… can decide what to show before anyone signs in. */
export const controlModesQuery = {
  queryKey: ['control-modes'] as const,
  queryFn: async (): Promise<string[]> => {
    try {
      const r = await api.controlModes.get()
      return Array.isArray(r.modes) ? r.modes.filter((m): m is ControlMode => typeof m === 'string') : []
    } catch {
      return []
    }
  },
  staleTime: 60_000,
}

/** The modes that are on: the config's list, else the server's `/v1/control-modes`. */
export function modesFrom(configModes: readonly string[] | null, fallback: readonly string[] | undefined): ReadonlySet<string> {
  return new Set(configModes ?? fallback ?? [])
}

export function useControlModes(): { modes: ReadonlySet<string>; isLoading: boolean } {
  const fromConfig = useAppConfig().features.control_modes
  const q = useQuery({ ...controlModesQuery, enabled: fromConfig === null })
  return { modes: modesFrom(fromConfig, q.data), isLoading: fromConfig === null && q.isPending }
}
